import { SubscriptionType } from "@common/types/billing";
import { PREMIUM_FEATURES } from "@common/flatRateBilling";
import { UserType } from "@common/types/user";
import { useUser } from "./useUser";
import { useBillingStatus } from "./useBillingStatus";
import { useTypedFlags } from "./useTypedFlags";

interface UseAllowFeatureOptions {
    disallowTrials?: boolean;
    shouldSkipSupportBypass?: boolean;
}

export function useFeatureAccessChecker() {
    const { bypassPaywall } = useTypedFlags();

    const user = useUser();
    const { subscriptionType, isTrialing, currentPlan, plans, isFetched } =
        useBillingStatus();

    const hasFeatureAccess = (
        feature: PREMIUM_FEATURES,
        options?: UseAllowFeatureOptions,
    ): boolean => {
        const { disallowTrials, shouldSkipSupportBypass } = options ?? {};

        if (bypassPaywall) {
            return true;
        }

        if (subscriptionType !== SubscriptionType.FLAT_RATE) {
            return true; // by default, features are available to subscribed users on old plans
        }

        if (user?.type === UserType.SUPPORT && !shouldSkipSupportBypass) {
            return true; // features are available for support users
        }

        if (disallowTrials && isTrialing) {
            return false;
        }

        const activePlan = plans.find((plan) => plan.plan === currentPlan);

        return Boolean(activePlan?.features.includes(feature));
    };

    return { hasFeatureAccess, isFetched: bypassPaywall ? true : isFetched };
}

export function useAllowFeature(
    feature: PREMIUM_FEATURES,
    options?: UseAllowFeatureOptions,
): { hasAccess: boolean; isFetched: boolean } {
    const { hasFeatureAccess, isFetched } = useFeatureAccessChecker();

    return { hasAccess: hasFeatureAccess(feature, options), isFetched };
}
